import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Remount-surviving text drafts, keyed by caller (item + field). Signing up flips
 * the data identity, which remounts the whole tree with a fresh graph: text typed
 * before the flip would die with the old instance (its unmount-save targets the
 * disposed graph), so drafts live outside React and rehydrate the next instance.
 */
const drafts = new Map<string, string>();

/**
 * Text field state that saves itself: commit on blur, on unmount, and after a
 * quiet period (1.5s) while typing. No Save button needed.
 *
 * With `draftKey`, unsaved text survives a remount (see above): a failed save keeps
 * the draft, and a fresh mount rehydrates it dirty so the next blur/unmount still
 * persists it. A draft matching the store value is already converged and dropped.
 */
export function useAutosaveField(
  initial: string,
  save: (value: string) => Promise<void>,
  draftKey?: string,
) {
  // One-shot start state (idempotent under StrictMode double-render).
  const startRef = useRef<{ value: string; dirty: boolean } | null>(null);
  if (startRef.current === null) {
    let value = initial;
    let dirty = false;
    if (draftKey !== undefined) {
      const d = drafts.get(draftKey);
      if (d !== undefined && d !== initial) {
        value = d;
        dirty = true;
      } else {
        drafts.delete(draftKey);
      }
    }
    startRef.current = { value, dirty };
  }
  const [value, setValue] = useState(startRef.current.value);
  const saveRef = useRef(save);
  saveRef.current = save;
  const stateRef = useRef({ value: startRef.current.value, dirty: startRef.current.dirty });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const commit = useCallback(() => {
    clearTimer();
    const s = stateRef.current;
    if (!s.dirty) return;
    s.dirty = false;
    const v = s.value;
    void saveRef.current(v).then(
      () => {
        if (draftKey !== undefined && stateRef.current.value === v) drafts.delete(draftKey);
      },
      () => {
        s.dirty = true;
      },
    );
  }, [clearTimer, draftKey]);

  const onChange = useCallback(
    (next: string) => {
      stateRef.current = { value: next, dirty: true };
      setValue(next);
      if (draftKey !== undefined) drafts.set(draftKey, next);
      clearTimer();
      timer.current = setTimeout(() => commit(), 1500);
    },
    [commit, clearTimer, draftKey],
  );

  useEffect(() => {
    if (stateRef.current.dirty) return;
    stateRef.current = { value: initial, dirty: false };
    setValue(initial);
  }, [initial]);

  // Flush on unmount so backgrounding the app never drops the last keystrokes.
  useEffect(() => () => commit(), [commit]);

  return { value, onChange, commit };
}
